import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useContactForm,
} from "../contexts/ContactFormContext";

import {
  getAllParrainages,
} from "../services/parrainageServices";

import {
  getAllDesinscriptions,
} from "../services/desinscriptionServices";

import {
  getAllRequests,
} from "../services/requestsServices";

import {
  getSubscriptionAlertsSummary,
} from "../services/subscriptionServices";

const toArrayCount = (value) =>
  Array.isArray(value)
    ? value.length
    : 0;

const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
};

const isPendingRequest = (
  request,
) => {
  return (
    String(
      request?.status || "",
    )
      .trim()
      .toLowerCase() ===
    "pending"
  );
};

const EMPTY_SUBSCRIPTION_ALERTS = {
  total: 0,

  renewals_to_prepare: 0,

  quotes_to_follow_up: 0,

  overdue_payments: 0,

  critical: 0,
};

export default function useDashboardAlerts({
  enabled = true,
} = {}) {
  const {
    unreadCountContactForm = 0,
  } = useContactForm();

  const [counters, setCounters] =
    useState({
      parrainages: 0,

      desinscriptions: 0,

      pendingRequests: 0,

      /*
       * À brancher plus tard
       * si nécessaire.
       */
      siteMessages: 0,
    });

  const [
    subscriptionAlerts,
    setSubscriptionAlerts,
  ] = useState(
    EMPTY_SUBSCRIPTION_ALERTS,
  );

  const [loading, setLoading] =
    useState(false);

  /* ==========================================================================
   * CHARGEMENT DES ALERTES
   * ========================================================================== */

  const refreshCounters =
    useCallback(async () => {
      if (!enabled) {
        return;
      }

      try {
        setLoading(true);

        const [
          parrainagesResult,

          desinscriptionsResult,

          requestsResult,

          subscriptionAlertsResult,
        ] =
          await Promise.allSettled([
            getAllParrainages(),

            getAllDesinscriptions(),

            getAllRequests(),

            getSubscriptionAlertsSummary(),
          ]);

        /* ------------------------------------------------------------------
         * REQUÊTES CLIENTS
         * ---------------------------------------------------------------- */

        const requests =
          requestsResult.status ===
            "fulfilled" &&
          Array.isArray(
            requestsResult.value,
          )
            ? requestsResult.value
            : [];

        const pendingRequests =
          requests.filter(
            isPendingRequest,
          );

        /* ------------------------------------------------------------------
         * COMPTEURS HISTORIQUES
         * ---------------------------------------------------------------- */

        setCounters((prev) => ({
          ...prev,

          parrainages:
            parrainagesResult.status ===
            "fulfilled"
              ? toArrayCount(
                  parrainagesResult.value,
                )
              : 0,

          desinscriptions:
            desinscriptionsResult.status ===
            "fulfilled"
              ? toArrayCount(
                  desinscriptionsResult.value,
                )
              : 0,

          pendingRequests:
            pendingRequests.length,
        }));

        /* ------------------------------------------------------------------
         * ABONNEMENTS
         * ---------------------------------------------------------------- */

        if (
          subscriptionAlertsResult.status ===
          "fulfilled"
        ) {
          const data =
            subscriptionAlertsResult.value ||
            {};

          setSubscriptionAlerts({
            total: toNumber(
              data.total,
            ),

            renewals_to_prepare:
              toNumber(
                data.renewals_to_prepare,
              ),

            quotes_to_follow_up:
              toNumber(
                data.quotes_to_follow_up,
              ),

            overdue_payments:
              toNumber(
                data.overdue_payments,
              ),

            critical: toNumber(
              data.critical,
            ),
          });
        } else {
          /*
           * Une erreur sur les abonnements
           * ne doit pas empêcher les autres
           * badges du dashboard de fonctionner.
           */
          setSubscriptionAlerts(
            EMPTY_SUBSCRIPTION_ALERTS,
          );
        }
      } catch (error) {
        console.error(
          "Erreur chargement alertes dashboard :",
          error,
        );

        setCounters((prev) => ({
          ...prev,

          parrainages: 0,

          desinscriptions: 0,

          pendingRequests: 0,
        }));

        setSubscriptionAlerts(
          EMPTY_SUBSCRIPTION_ALERTS,
        );
      } finally {
        setLoading(false);
      }
    }, [enabled]);

  /* ==========================================================================
   * PREMIER CHARGEMENT
   * ========================================================================== */

  useEffect(() => {
    refreshCounters();
  }, [refreshCounters]);

  /* ==========================================================================
   * RAFRAÎCHISSEMENT GLOBAL
   *
   * Une page du dashboard peut déclencher :
   *
   * window.dispatchEvent(
   *   new Event("dashboard-alerts:refresh")
   * );
   *
   * Cela permet de mettre immédiatement
   * à jour le badge de la sidebar.
   * ========================================================================== */

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleRefresh =
      () => {
        refreshCounters();
      };

    window.addEventListener(
      "dashboard-alerts:refresh",
      handleRefresh,
    );

    return () => {
      window.removeEventListener(
        "dashboard-alerts:refresh",
        handleRefresh,
      );
    };
  }, [
    enabled,
    refreshCounters,
  ]);

  /* ==========================================================================
   * BADGES MENU
   * ========================================================================== */

  const badgeMap = useMemo(
    () => ({
      contactForms:
        unreadCountContactForm,

      parrainages:
        counters.parrainages,

      desinscriptions:
        counters.desinscriptions,

      pendingRequests:
        counters.pendingRequests,

      siteMessages:
        counters.siteMessages,

      /*
       * Nouveau badge :
       * Abonnements.
       */
      subscriptions:
        subscriptionAlerts.total,
    }),
    [
      unreadCountContactForm,
      counters,
      subscriptionAlerts.total,
    ],
  );

  /* ==========================================================================
   * CENTRE D'ALERTES HISTORIQUE
   * ========================================================================== */

  const alerts = useMemo(
    () => [
      {
        key: "contactForms",

        label:
          "Messages formulaire",

        description:
          "Messages reçus depuis le formulaire de contact.",

        count:
          unreadCountContactForm,

        path:
          "/appli/contact-form",

        tone: "sky",
      },

      {
        key:
          "pendingRequests",

        label:
          "Requêtes en attente",

        description:
          "Demandes clients avec le statut Pending à traiter.",

        count:
          counters.pendingRequests,

        path:
          "/appli/site-activity",

        tone: "rose",
      },

      {
        key: "subscriptions",

        label:
          "Abonnements",

        description:
          "Renouvellements, devis et règlements nécessitant une intervention.",

        count:
          subscriptionAlerts.total,

        path:
          "/appli/subscriptions",

        tone:
          subscriptionAlerts.critical >
          0
            ? "rose"
            : "amber",
      },

      {
        key: "siteMessages",

        label:
          "Messages sites",

        description:
          "Messages reçus depuis tes sites clients.",

        count:
          counters.siteMessages,

        path: null,

        tone: "violet",

        pending: true,
      },

      {
        key: "parrainages",

        label: "Parrainages",

        description:
          "Demandes ou entrées de parrainage.",

        count:
          counters.parrainages,

        path:
          "/appli/parrainage",

        tone: "emerald",
      },

      {
        key:
          "desinscriptions",

        label:
          "Désinscriptions",

        description:
          "Demandes de désinscription à contrôler.",

        count:
          counters.desinscriptions,

        path:
          "/appli/desinscriptions-admin",

        tone: "amber",
      },
    ],
    [
      unreadCountContactForm,
      counters,
      subscriptionAlerts,
    ],
  );

  const totalAlerts =
    useMemo(
      () =>
        alerts.reduce(
          (
            total,
            alert,
          ) =>
            total +
            Number(
              alert.count || 0,
            ),
          0,
        ),
      [alerts],
    );

  return {
    alerts,

    badgeMap,

    totalAlerts,

    loading,

    refreshCounters,

    /*
     * Informations détaillées
     * utilisées notamment par
     * AsideMenu.
     */
    subscriptionAlerts,
  };
}